import apiClient from '../lib/api/client';
import config, { getApiUrl } from '../config/config';
import { formatMessagePreviewShort } from '../components/chat/utils/formatters';

/** Tab key for internal-chat "All" pill + NotificationContext (shared with App when route unmounted). */
export const INTERNAL_CHAT_ALL_TAB_NOTIFY = 'InternalChatAll';
/** Prefix conversation ids so internal chat badges never clash with Contact Center numeric ids. */
export const internalChatConversationNotifyKey = (id) => `internal-${id}`;

/**
 * Normalize internal.conversation.new.message payloads (flat or after Internal.Notification unwrap).
 * @param {object} data - handleMessage root object (must include event / message / conversation as sent by backend)
 * @param {object|null} conversationPayload - When parser used event+conversation branch, the conversation object
 * @returns {{ conversationId: *, message: object|undefined, conversation: object|null }|null}
 */
export const normalizeInternalConversationNewMessage = (data, conversationPayload = null) => {
  if (!data || typeof data !== 'object') return null;
  const conversation = data.conversation ?? conversationPayload ?? null;
  const message = data.message;
  const conversationId =
    conversation?.id ??
    message?.conversation ??
    message?.conversation_id ??
    data.conversation_id;
  if (conversationId == null || conversationId === '') return null;
  return { conversationId, message, conversation };
};

/**
 * Conversation id from internal-chat indicator payloads (same shapes as normalizeInternalConversationNewMessage).
 * Used when listeners receive a partial envelope, not the full handleMessage root.
 */
export const resolveInternalConversationNotifyPayloadId = (payload) => {
  if (payload == null || typeof payload !== 'object') return null;
  const conversation = payload.conversation;
  const message = payload.message;
  const conversationId =
    payload.id ??
    (conversation && typeof conversation === 'object' ? conversation.id : undefined) ??
    (message && typeof message === 'object'
      ? message.conversation ?? message.conversation_id
      : undefined) ??
    payload.conversation_id;
  if (conversationId == null || conversationId === '') return null;
  return conversationId;
};

/** True if the new-message event is from the logged-in user (skip unread indicators / sound). */
export const isInternalChatNewMessageFromCurrentUser = (message, currentUserId) => {
  if (!message || currentUserId == null) return false;
  const uid =
    message.sender_user ??
    message.sender_user_id ??
    message.sender?.id ??
    message.user ??
    message.user_id;
  if (uid == null || uid === '') return false;
  return String(uid) === String(currentUserId);
};

const conversationActionUrl = (conversationId, segment) =>
  `${config.API.BASE_URL}/api/chats/conversations/${encodeURIComponent(String(conversationId))}/${segment}/`;

const formatRoleLabel = (role = '') => {
  const r = String(role || '');
  if (!r) return 'Member';
  return r.charAt(0).toUpperCase() + r.slice(1).toLowerCase();
};

const getInitialsFromName = (name = '') => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return String(name).slice(0, 2).toUpperCase();
};

const formatRelativeShort = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';

  const now = new Date();
  const isToday =
    d.getDate() === now.getDate() &&
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear();

  if (isToday) {
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  }

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday =
    d.getDate() === yesterday.getDate() &&
    d.getMonth() === yesterday.getMonth() &&
    d.getFullYear() === yesterday.getFullYear();

  if (isYesterday) {
    return 'Yesterday';
  }

  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
};

const extractLastMessageText = (item) => {
  const lm = item.last_message ?? item.lastMessage ?? item.preview;
  if (lm == null || lm === '') return null;
  if (typeof lm === 'string') return lm.trim() || null;
  const t = lm.text ?? lm.content ?? lm.body ?? lm.message ?? '';
  return typeof t === 'string' ? (t.trim() || null) : null;
};

const mapMemberRow = (m) => {
  if (!m) return null;
  const uid = m.user ?? m.user_id;
  const name =
    m.full_name ??
    m.fullName ??
    m.fullname ??
    m.name ??
    m.user_name ??
    (uid != null && uid !== '' ? `User ${uid}` : '');
  if (!name) return null;
  const nUid = uid != null && uid !== '' ? Number(uid) : NaN;
  return {
    name,
    initials: getInitialsFromName(name),
    role: formatRoleLabel(m.role ?? 'agent'),
    online: !!(m.is_online ?? m.online),
    email:
      m.email ??
      m.user_email ??
      m.mail ??
      m.user?.email ??
      null,
    ...(Number.isFinite(nUid) ? { userId: nUid } : {}),
  };
};

const resolveInternalDirectDisplayName = (item, currentUserId) => {
  const parts = Array.isArray(item.participants) ? item.participants : [];
  const myIdRaw = currentUserId != null ? Number(currentUserId) : NaN;
  let other = null;
  if (Number.isFinite(myIdRaw)) {
    other = parts.find((p) => Number(p.user) !== myIdRaw);
  }
  if (!other && parts.length) {
    other =
      parts.find((p) => String(p.role || '').toLowerCase() !== 'owner') ??
      parts[parts.length - 1];
  }

  const rootName = item.user_name ?? item.userName ?? item.other_user_fullname;
  const otherName = other?.user_name ?? other?.userName ?? other?.fullname ?? other?.name;
  const trimmed = (v) => (typeof v === 'string' ? v.trim() : v);

  return (
    trimmed(rootName) ||
    trimmed(otherName) ||
    (other?.user != null && other.user !== '' ? `User ${other.user}` : null) ||
    trimmed(item.subject) ||
    'Chat'
  );
};

export const mapInternalChatListItem = (item, options = {}) => {
  const { currentUserId } = options;
  const id = String(item.id ?? item.conversation_id ?? '');
  if (!id) return null;

  const channelNorm = (item.channel ?? '').toString().trim().toLowerCase();
  const isGroup =
    item.type === 'group' ||
    item.conversation_type === 'group' ||
    item.chat_type === 'group' ||
    item.is_group === true ||
    channelNorm === 'group';
  const isInternal = channelNorm === 'internal';

  let name;
  if (isGroup) {
    name =
      item.subject ??
      item.group_name ??
      item.title ??
      item.name ??
      'Group';
  } else if (isInternal) {
    name = resolveInternalDirectDisplayName(item, currentUserId);
  } else {
    name =
      item.name ??
      item.title ??
      item.group_name ??
      item.subject ??
      item.other_user_fullname ??
      item.fullname ??
      item.user_name ??
      'Chat';
  }

  const lastRaw = extractLastMessageText(item);
  const lastMessage = formatMessagePreviewShort(lastRaw, 24);

  const unread = Number(item.unread_count ?? item.unread ?? item.unread_messages ?? 0) || 0;

  const timeField =
    item.last_message_at ??
    item.updated_at ??
    item.last_activity ??
    item.modified_at ??
    item.created_at;

  const membersRaw = item.members ?? item.participants ?? item.group_members;
  let members;
  if (Array.isArray(membersRaw) && membersRaw.length) {
    members = membersRaw.map(mapMemberRow).filter(Boolean);
  }

  return {
    id,
    name,
    initials: getInitialsFromName(name),
    lastMessage,
    time: formatRelativeShort(timeField),
    unread,
    type: isGroup ? 'group' : 'direct',
    channel: item.channel != null ? String(item.channel) : undefined,
    online: !!(item.is_online ?? item.other_user_online),
    favourite: !!(item.is_favourite ?? item.is_favorite ?? item.favourite),
    ownerId: item.owner ?? null,
    createdAt: item.created_at ?? null,
    ...(members && members.length ? { members } : {}),
  };
};

export const INTERNAL_CHAT_LIST_LIMIT = 30;

/** Parses the cursor-paginated envelope `{ data: { results, pagination: { has_more, next_cursor } } }`. */
const extractInternalChatListPage = (payload, options = {}) => {
  const data = payload?.data;
  let rows = [];
  let pagination = {};
  if (data && Array.isArray(data.results)) {
    rows = data.results;
    pagination = data.pagination || payload?.pagination || {};
  } else if (Array.isArray(data)) {
    rows = data;
  } else if (Array.isArray(payload)) {
    rows = payload;
  }
  return {
    rows: rows.map((row) => mapInternalChatListItem(row, options)).filter(Boolean),
    hasMore: !!pagination.has_more,
    nextCursor: pagination.next_cursor ?? null,
  };
};

const fetchInternalChatListEndpoint = async (endpointKey, { currentUserId, cursor, limit = INTERNAL_CHAT_LIST_LIMIT, search, signal } = {}) => {
  const url = getApiUrl('INTERNAL_CHAT', endpointKey);
  const { data } = await apiClient.get(url, {
    params: { limit, cursor: cursor || undefined, search: search || undefined },
    ...(signal ? { signal } : {}),
  });
  return extractInternalChatListPage(data, { currentUserId });
};

/** All tab — GET /api/chats/internal-chats/, cursor-paginated (limit 30 default). */
export const fetchInternalChatListPage = (params) =>
  fetchInternalChatListEndpoint('GET_LIST', params);

/** Fav tab — GET /api/chats/internal-chats/favorite, cursor-paginated (limit 30 default). */
export const fetchFavoriteInternalChatListPage = (params) =>
  fetchInternalChatListEndpoint('GET_LIST_FAVORITE', params);

/** Group tab — GET /api/chats/internal-chats/group, cursor-paginated (limit 30 default). */
export const fetchGroupInternalChatListPage = (params) =>
  fetchInternalChatListEndpoint('GET_LIST_GROUP', params);

/** Human-readable message from create-group (or generic) API JSON. */
export const getInternalChatApiMessage = (payload) => {
  if (payload == null) return '';
  if (typeof payload === 'string') return payload;
  const root = payload.message ?? payload.detail;
  if (typeof root === 'string' && root) return root;
  const d = payload.data;
  if (d && typeof d === 'object' && !Array.isArray(d)) {
    const m = d.message ?? d.detail;
    if (typeof m === 'string' && m) return m;
  }
  if (payload.isSuccess === true && typeof payload.message === 'string') return payload.message;
  return '';
};

const pickCreatedConversationId = (payload) => {
  const conv = payload?.data?.conversation;
  if (conv && typeof conv === 'object' && conv.id != null) {
    return String(conv.id);
  }
  const raw = payload?.data ?? payload;
  if (raw == null) return null;
  if (typeof raw !== 'object') return null;
  const id = raw.id ?? raw.conversation_id ?? raw.chat_id ?? raw.conversationId;
  if (id == null || id === '') return null;
  return String(id);
};

export const mapCreateInternalChatConversationToListItem = (conversation, { displayName } = {}) => {
  if (!conversation || conversation.id == null) return null;

  const channelNorm = (conversation.channel ?? '').toString().trim().toLowerCase();
  const isGroup = channelNorm === 'group';

  const name =
    (displayName && String(displayName).trim()) ||
    (conversation.subject && String(conversation.subject).trim()) ||
    'Chat';

  return {
    id: String(conversation.id),
    name,
    initials: getInitialsFromName(name),
    lastMessage: formatMessagePreviewShort(null, 24),
    time: formatRelativeShort(conversation.updated_at ?? conversation.created_at),
    unread: 0,
    type: isGroup ? 'group' : 'direct',
    channel: conversation.channel != null ? String(conversation.channel) : undefined,
    online: false,
    favourite: false,
    ownerId: conversation.owner ?? null,
    createdAt: conversation.created_at ?? null,
  };
};

export const createInternalGroupChat = async ({ subject, userIds }) => {
  const url = getApiUrl('INTERNAL_CHAT', 'CREATE_GROUP');
  const ids = userIds.map((id) => {
    const n = Number(id);
    if (!Number.isFinite(n)) throw new Error('Invalid user id in selection');
    return n;
  });
  const { data } = await apiClient.post(url, {
    subject: String(subject || '').trim(),
    user_ids: ids,
  });
  return { data, createdId: pickCreatedConversationId(data) };
};

export const createInternalChat = async (userId) => {
  const url = getApiUrl('INTERNAL_CHAT', 'CREATE_DIRECT');
  const n = Number(userId);
  if (!Number.isFinite(n)) throw new Error('Invalid user id');
  const { data } = await apiClient.post(url, { user_id: n });
  const conversation = data?.data?.conversation ?? null;
  const createdId =
    conversation?.id != null ? String(conversation.id) : pickCreatedConversationId(data);
  return { data, conversation, createdId };
};

const mapActiveMemberRow = (m) => {
  if (!m) return null;
  const userId = m.user ?? m.user_id ?? m.userId;
  if (userId == null || userId === '') return null;
  const nested = m.user && typeof m.user === 'object' ? m.user : null;
  const name =
    m.full_name ??
    m.fullName ??
    m.fullname ??
    m.user_name ??
    m.username ??
    m.name ??
    nested?.full_name ??
    nested?.fullName ??
    nested?.fullname ??
    nested?.name ??
    nested?.user_name ??
    `User ${userId}`;
  const roleRaw = m.role ?? 'Member';
  return {
    userId: Number(userId),
    name: String(name).trim() || `User ${userId}`,
    initials: getInitialsFromName(name),
    role: formatRoleLabel(roleRaw),
    online: !!(m.is_online ?? m.online),
    email:
      m.email ??
      m.user_email ??
      m.mail ??
      nested?.email ??
      null,
  };
};

export const normalizeActiveMembersResponse = (payload) => {
  let rows = [];
  if (payload?.isSuccess && Array.isArray(payload.data)) rows = payload.data;
  else if (Array.isArray(payload?.data)) rows = payload.data;
  else if (Array.isArray(payload)) rows = payload;
  return rows.map(mapActiveMemberRow).filter(Boolean);
};

export const fetchConversationActiveMembers = async (conversationId, requestOptions = {}) => {
  const { signal } = requestOptions;
  const url = conversationActionUrl(conversationId, 'active-members');
  const { data } = await apiClient.get(url, { ...(signal ? { signal } : {}) });
  if (data?.isSuccess === false) {
    const apiMessage =
      data?.error?.message ??
      data?.message ??
      'Could not load group members';
    throw new Error(apiMessage);
  }
  return normalizeActiveMembersResponse(data);
};

export const addConversationMembers = async (conversationId, userIds) => {
  const url = conversationActionUrl(conversationId, 'add-member');
  const ids = (userIds || []).map((id) => {
    const n = Number(id);
    if (!Number.isFinite(n)) throw new Error('Invalid user id in selection');
    return n;
  });
  if (ids.length === 0) throw new Error('At least one user id is required');
  const { data } = await apiClient.post(url, { user_ids: ids });
  return data;
};

export const removeConversationMember = async (conversationId, userId) => {
  const url = conversationActionUrl(conversationId, 'remove-member');
  const n = Number(userId);
  if (!Number.isFinite(n)) throw new Error('Invalid user id');
  const { data } = await apiClient.post(url, { user_id: n });
  return data;
};

export const changeConversationSubject = async (conversationId, subject) => {
  const url = conversationActionUrl(conversationId, 'change-subject');
  const trimmed = String(subject || '').trim();
  if (!trimmed) throw new Error('Subject is required');
  const { data } = await apiClient.post(url, { subject: trimmed });
  return data;
};

export const leaveConversationGroup = async (conversationId) => {
  const url = conversationActionUrl(conversationId, 'leave-group');
  const { data } = await apiClient.post(url, {});
  return data;
};

export const addConversationFavorite = async (conversationId) => {
  const url = conversationActionUrl(conversationId, 'add-favorite');
  const { data } = await apiClient.post(url, {});
  return data;
};

export const removeConversationFavorite = async (conversationId) => {
  const url = conversationActionUrl(conversationId, 'remove-favorite');
  const { data } = await apiClient.post(url, {});
  return data;
};

export default {
  fetchInternalChatListPage,
  fetchFavoriteInternalChatListPage,
  fetchGroupInternalChatListPage,
  createInternalGroupChat,
  createInternalChat,
  fetchConversationActiveMembers,
  addConversationMembers,
  removeConversationMember,
  changeConversationSubject,
  leaveConversationGroup,
  addConversationFavorite,
  removeConversationFavorite,
  getInternalChatApiMessage,
  mapInternalChatListItem,
  mapCreateInternalChatConversationToListItem,
};